import React, { useCallback, useMemo, useState } from 'react'
import { Box, Button, CircularProgress } from '@mui/material'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import {
  AdGroupBudgetPlanPartsFragment,
  AdGroupCreateInput,
  AdGroupInput,
  useAdGroupBudgetPlansQuery,
} from '../../../../../__generated__/graphql'
import SectionTitle from '../../../../../components/form/SectionTitle'
import { FormDialog } from '../../../../../components/layout/FormDialog'
import { Section } from '../../../../../components/layout/Section'
import { useAppTranslations } from '../../../../../utils/hooks/useAppTranslations'
import BudgetPlanForm from './BudgetPlanForm'
import BudgetPlansTable from './BudgetPlansTable'
import { filterFutureBudgetPlans, sortBudgetPlansByPlannedDate } from './budgetPlanForm.utils'

interface BudgetPlansProps {
  adGroupId: string
  adgroup: AdGroupInput | AdGroupCreateInput
}

const BudgetPlans: React.FC<BudgetPlansProps> = ({ adGroupId, adgroup }): JSX.Element => {
  const t = useAppTranslations()
  const [open, setOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [editingPlan, setEditingPlan] = useState<AdGroupBudgetPlanPartsFragment | undefined>()
  const { data, loading } = useAdGroupBudgetPlansQuery({
    variables: { id: adGroupId },
    fetchPolicy: 'cache-and-network',
  })

  const futurePlans = useMemo(
    () => filterFutureBudgetPlans(data?.adGroup.budgetPlans ?? []),
    [data?.adGroup.budgetPlans]
  )

  const displayedPlans = useMemo(
    () => sortBudgetPlansByPlannedDate((data?.adGroup.budgetPlans ?? []).filter((plan) => !plan.processed)),
    [data?.adGroup.budgetPlans]
  )

  const handleEdit = useCallback((plan: AdGroupBudgetPlanPartsFragment) => {
    setEditingPlan(plan)
    setEditOpen(true)
  }, [])

  return (
    <>
      <Section>
        <SectionTitle title="adgroup.budgetAndDates.budgetPlans.title" />
        <Box>
          <Button
            onClick={() => setOpen(true)}
            variant="text"
            color="secondary"
            startIcon={<CalendarMonthOutlinedIcon />}
            data-testid="planBudgetChangesButton"
          >
            {t('adgroup.budgetAndDates.budgetPlans.button')}
          </Button>
          {displayedPlans.length > 0 && (
            <Box data-testid="budgetPlansInlineTable">
              <BudgetPlansTable adGroupId={adGroupId} plans={displayedPlans} readOnly />
            </Box>
          )}
        </Box>
      </Section>
      {open && (
        <FormDialog
          open={open}
          setOpen={setOpen}
          title={t('adgroup.budgetAndDates.budgetPlans.title')}
          maxWidth="lg"
          fullWidth
        >
          {loading && !data ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <>
              <BudgetPlansTable adGroupId={adGroupId} plans={displayedPlans} onEdit={handleEdit} />
              <BudgetPlanForm adGroupId={adGroupId} adgroup={adgroup} plans={futurePlans} />
            </>
          )}
        </FormDialog>
      )}
      {editingPlan && (
        <FormDialog
          open={editOpen}
          setOpen={setEditOpen}
          title={t('adgroup.budgetAndDates.budgetPlans.editTitle')}
          maxWidth="lg"
          fullWidth
        >
          <BudgetPlanForm
            adGroupId={adGroupId}
            adgroup={adgroup}
            plans={futurePlans}
            editingPlan={editingPlan}
            onDone={() => setEditOpen(false)}
          />
        </FormDialog>
      )}
    </>
  )
}

export default BudgetPlans
